import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EntityServices } from "../Services/EntityServices";
import { ILogIn } from "../types/types";
import { setSession } from "../auth/utils";
import { AuthContext } from "../auth/authContext";
import ShowSnackbar from "../Services/snackbar";
import { END_POINTS_PATH } from "../routes/paths";
import axios from "../utils/axios";
import { HOST_API_URL_V2 } from "../config";

function getErrorMessage(error: any, fallback: string) {
  return typeof error?.statusCode === "number" && error?.message
    ? error.message
    : fallback;
}

function LogInPage() {
  const [credentials, SetCredentials] = useState({} as ILogIn);
  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [challengeToken, setChallengeToken] = useState<string | undefined>();
  const [code, setCode] = useState("");
  const [togglePasswordVisibility, setTogglePasswordVisibility] = useState(false);

  const { setAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  const togglePasswordVisibilityHandler = () => {
    setTogglePasswordVisibility((prev) => !prev);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    SetCredentials((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const onSubmitStep1 = async () => {
    const services = new EntityServices();
    if (!credentials.username) {
      ShowSnackbar("Email is required", "danger");
      return;
    }
    if (!credentials.password) {
      ShowSnackbar("Password is required", "danger");
      return;
    }
    try {
      setLoading(true);
      var response = await axios.post(HOST_API_URL_V2 + END_POINTS_PATH.LogIn, credentials);
      if (response && response.status === 200) {
        setStep(2);
        ShowSnackbar("Enter the 2FA code sent to your registered email.", "info");
      } else {
        ShowSnackbar(response?.data?.message || "Invalid Credentials", "danger");
      }
    } catch (error: any) {
      ShowSnackbar(getErrorMessage(error, "Login failed"), "danger");
    } finally {
      setLoading(false);
    }
  };

  const onSubmitStep2 = async () => {
    try {
      setLoading(true);
      const services = new EntityServices();
      var response = await axios.post(HOST_API_URL_V2 + END_POINTS_PATH.Verify2FA, {
        username: credentials.username,
        password: credentials.password,
        token: code.trim(),
      });
      if (response && response.status === 200) {
        setSession(response.data.data);
        setAuthenticated(true);
        ShowSnackbar("Welcome", "primary");
        navigate('/');
      } else {
        ShowSnackbar(response?.data?.message || "Invalid 2FA code", "danger");
      }
    } catch (error: any) {
      ShowSnackbar(getErrorMessage(error, "2FA verification failed"), "danger");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="authX-page-wrapper d-flex justify-content-center align-items-center">
      <div className="authX-container shadow-lg">

        <div className="authX-left d-none d-md-flex flex-column justify-content-center">
          <div className="authX-left-content">
            <img src="./assets/images/DahabGreen.png" className="authX-left-logo mb-4" />
            <h2 className="authX-left-title">Unified Remittance API</h2>
            <p className="authX-left-text">
              A secure gateway for our partners to access remittance services globally. Fast, reliable, and easy to integrate.
            </p>

            <div className="authX-badges mt-4">
              <span className="authX-badge">
                <i className="zmdi zmdi-shield-check me-1"></i> Global Remittance
              </span>
              <span className="authX-badge">
                <i className="zmdi zmdi-lock-outline me-1"></i> Instant Transfers
              </span>
              <span className="authX-badge">
                <i className="zmdi zmdi-bug me-1"></i> Bank & wallet Payouts
              </span>
              <span className="authX-badge">
                <i className="zmdi zmdi-bug me-1"></i> Developer Friendly
              </span>
            </div>
          </div>
        </div>

        <div className="authX-right">
          <div className="authX-form-box">

            <h3 className="authX-form-title mb-4">
              {step === 1 ? "Sign In" : "Verify Token"}
            </h3>

            {/* STEP 1 */}
            {step === 1 && (
              <>
                <div className="authX-input-wrap mb-3">
                  <label className="authX-label">Email</label>
                  <div className="position-relative">
                    <input
                      type="text"
                      name="username"
                      value={credentials.username || ""}
                      onChange={handleChange}
                      className="form-control authX-input"
                      placeholder="Enter Email"
                      disabled={loading}
                      required
                    />
                    <span className="authX-input-icon">
                      <i className="zmdi zmdi-account"></i>
                    </span>
                  </div>
                </div>

                <div className="authX-input-wrap mb-4">
                  <label className="authX-label">Password</label>
                  <div className="position-relative">
                    <input
                      name="password"
                      value={credentials.password || ""}
                      onChange={handleChange}
                      className="form-control authX-input"
                      placeholder="Enter Password"
                      type={togglePasswordVisibility ? "text" : "password"}
                      disabled={loading}
                      required
                    />
                    {togglePasswordVisibility ? (
                      <span
                        className="authX-input-icon authX-input-icon-clickable"
                        onClick={togglePasswordVisibilityHandler}
                      >
                        <i className="zmdi zmdi-eye-off"></i>
                      </span>
                    ) : (
                      <span
                        className="authX-input-icon authX-input-icon-clickable"
                        onClick={togglePasswordVisibilityHandler}
                      >
                        <i className="zmdi zmdi-eye"></i>
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={onSubmitStep1}
                  disabled={loading}
                  className="btn authX-btn w-100"
                >
                  {loading ? "Processing..." : "Continue"}
                </button>
              </>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <>
                <p className="small text-muted mb-3">
                  A verification code has been sent.
                </p>

                <div className="authX-input-wrap mb-4">
                  <label className="authX-label">Verification Code</label>
                  <div className="position-relative">
                    <input
                      type="text"
                      maxLength={8}
                      value={code}
                      onChange={(e) =>
                        setCode(e.target.value.replace(/[^0-9A-Za-z]/g, ""))
                      }
                      className="form-control authX-input text-center"
                      placeholder="Enter Code"
                      disabled={loading}
                    />
                    <span className="authX-input-icon">
                      <i className="zmdi zmdi-key"></i>
                    </span>
                  </div>
                </div>
                <div className="d-flex justify-content-end mb-3">
                  <p>
                    This is test environment. Use any six-digit code like <strong>123456</strong>.
                  </p>
                </div>

                <button
                  onClick={onSubmitStep2}
                  disabled={loading}
                  className="btn authX-btn w-100"
                >
                  {loading ? "Verifying..." : "Verify & Sign In"}
                </button>

                <button
                  className="btn btn-link text-muted small mt-3"
                  onClick={() => {
                    setStep(1);
                    setCode("");
                    setChallengeToken(undefined);
                  }}
                  disabled={loading}
                >
                  Back
                </button>
              </>
            )}

            <div className="d-md-flex flex-row justify-content-between align-items-center mt-5 authX-form-footer ">
              <span className="authX-badge bg-light text-dark">
                <i className="zmdi zmdi-shield-check me-1"></i> Secure
              </span>
              <span className="authX-badge bg-light text-dark">
                <i className="zmdi zmdi-shield-check me-1"></i> 2FA Enabled
              </span></div>

          </div>
        </div>

      </div>
    </div>
  );
}

export default LogInPage;
